import { Alert, Linking, Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Caption, ThemedText, Body } from '@/components/ui/Text';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { PLAYBOOKS, type Playbook, type PlaybookStep } from '@/data/playbooks';

type PlaybookSectionProps = {
  collaborators?: Array<{ name?: string; email?: string }>;
  onMessageCollaborators?: (message: string) => void;
};

const openMapsSearch = async (q: string) => {
  const url = `https://maps.google.com/?q=${encodeURIComponent(q)}`;
  try {
    await Linking.openURL(url);
  } catch {
    Alert.alert('Error', 'Could not open maps.');
  }
};

const renderStep = (
  theme: ReturnType<typeof useTheme>,
  step: PlaybookStep,
  onMessageCollaborators?: (message: string) => void,
) => {
  const hasAction = !!step.actionType && !!step.actionPayload && !!step.actionLabel;
  return (
    <View
      key={`${step.text}:${step.actionLabel || ''}`}
      style={{
        padding: theme.spacing(1),
        borderRadius: theme.radii.lg,
        backgroundColor: theme.colors.borderMuted + '12',
        gap: theme.spacing(0.75),
      }}
    >
      <Body style={{ fontSize: 13, lineHeight: 19 }}>{step.text}</Body>
      {hasAction && (
        <Button
          label={step.actionLabel!}
          size="small"
          variant="secondary"
          onPress={() => {
            const p = step.actionPayload!;
            if (p.kind === 'message_collaborators') {
              if (!onMessageCollaborators) {
                Alert.alert('Not available', 'Messaging collaborators is not available here.');
                return;
              }
              onMessageCollaborators(p.value);
              return;
            }
            if (p.kind === 'open_maps_query') {
              void openMapsSearch(p.value);
              return;
            }
            if (p.kind === 'open_url') {
              Linking.openURL(p.value).catch(() => Alert.alert('Error', 'Could not open link.'));
              return;
            }
          }}
          icon={
            step.actionType === 'message' ? (
              <Ionicons name="send-outline" size={16} color={theme.colors.primary} />
            ) : step.actionType === 'navigate' ? (
              <Ionicons name="map-outline" size={16} color={theme.colors.primary} />
            ) : (
              <Ionicons name="call-outline" size={16} color={theme.colors.primary} />
            )
          }
        />
      )}
    </View>
  );
};

const PlaybookCard = ({
  playbook,
  onMessageCollaborators,
}: {
  playbook: Playbook;
  onMessageCollaborators?: (message: string) => void;
}) => {
  const theme = useTheme();
  return (
    <Card variant="outlined" style={{ padding: theme.spacing(1.25) }}>
      <View style={{ gap: theme.spacing(1) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75) }}>
          <Ionicons name={playbook.icon as any} size={18} color={theme.colors.textSecondary} />
          <ThemedText variant="bodyStrong" style={{ fontSize: 14, fontWeight: '800' }}>
            {playbook.title}
          </ThemedText>
        </View>
        <View style={{ gap: theme.spacing(0.75) }}>
          {playbook.steps.map((s) => renderStep(theme, s, onMessageCollaborators))}
        </View>
      </View>
    </Card>
  );
};

export function PlaybookSection({ onMessageCollaborators }: PlaybookSectionProps) {
  const theme = useTheme();

  return (
    <CollapsibleSection title="Playbooks" icon="warning-outline" defaultExpanded={false} variant="outlined">
      <View style={{ gap: theme.spacing(1.25) }}>
        <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
          Quick guidance for common situations. Not real-time monitoring.
        </Caption>
        {PLAYBOOKS.map((p) => (
          <PlaybookCard key={p.id} playbook={p} onMessageCollaborators={onMessageCollaborators} />
        ))}
        <Pressable
          onPress={() => {
            Alert.alert(
              'Safety note',
              'If there is an emergency, use local emergency services immediately.',
              [{ text: 'OK' }],
            );
          }}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1, paddingVertical: theme.spacing(0.5) })}
        >
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, textAlign: 'center' }}>
            Emergency? Use local emergency services.
          </Caption>
        </Pressable>
      </View>
    </CollapsibleSection>
  );
}

